import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { useParams } from 'react-router';
import type { GetAudienceQuery } from '../../../../../graphql/query/audiences/GetAudience.generated';
import { AllowedOrganizations } from './components/AllowedOrganizations/AllowedOrganizations';
import type { AudienceShare } from './components/AllowedOrganizations/types';
import { AudienceDetails } from './components/AudienceDetails/AudienceDetails';
import { AudienceOptions } from './components/AudienceOptions/AudienceOptions';
import { DealIds } from './components/DealIds/DealIds';
import { MultiPartnerPricingDetails } from './components/MultiPartnerPricingDetails';
import { OnBehalfOf } from './components/OnBehalfOf/OnBehalfOf';
import { Provider } from './components/Provider/Provider';
import { useAudienceDetails } from './hooks/useAudienceDetails';
import { isAudiencePricing } from './utils/isAudiencePricing';

const AudienceDetailsSections = ({
  audienceId,
  data,
}: {
  audienceId: string;
  data: NonNullable<GetAudienceQuery['audience_by_pk']>;
}) => {
  const audiencePricing = isAudiencePricing(data.pricing) ? data.pricing : undefined;
  const showMultiPartnerPricing = Boolean(data.is_multi_partner && audiencePricing);

  return (
    <Stack>
      <OnBehalfOf accountId={data.account_id} />
      <Provider accountId={data.account_id} providerId={data.direct_audience_provider} />
      <AudienceOptions type={data.export_type} exportTargets={data.export_targets} />
      <AudienceDetails name={data.name} description={data.description} />
      {showMultiPartnerPricing && audiencePricing && <MultiPartnerPricingDetails pricing={audiencePricing} />}
      <DealIds audienceId={audienceId} />
      <AllowedOrganizations audienceShares={data.audience_share as AudienceShare[]} />
    </Stack>
  );
};

export function AudienceDetailsTab() {
  const { audienceUid = '' } = useParams<{
    audienceUid: string;
  }>();
  const { data, error, loading } = useAudienceDetails(audienceUid);

  if (loading) {
    return <Loader />;
  }

  if (error || !data) {
    return <Alert severity="error">Failed to fetch audience {audienceUid}.</Alert>;
  }

  return <AudienceDetailsSections audienceId={audienceUid} data={data} />;
}
